import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  CalendarDays, 
  Info, 
  ShieldCheck, 
  MapPin, 
  CheckCircle2, 
  Sparkles, 
  ArrowRight, 
  X, 
  HeartHandshake, 
  Video, 
  XCircle, 
  Image as ImageIcon, 
  ChevronLeft,
  ChevronRight,
  Clock, 
  Lock, 
  Compass, 
  Award, 
  Layers, 
  AlertCircle 
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { toast } from 'react-toastify';
import Button from '../common/design-system/Button';
import RichTextDisplay from '../common/RichTextDisplay';
import styles from './PublicCollabLanding.module.scss';
import clsx from 'clsx';
import { acceptRecipientInvite, declineRecipientInvite, getRecipientInviteInfo } from '../../services/api';
import { announcePwaInstallOpportunity } from '../../utils/pwa';

const luxuryEase = [0.23, 1, 0.32, 1];

function AboutFothModal({ open, onClose }) {
  const { t } = useTranslation('creator');

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={styles['modal-overlay']}
            onClick={onClose}
          />
          <div className={styles['foth-modal-wrapper']}>
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.25, ease: luxuryEase }}
              className={styles['about-foth-modal']}
              role="dialog"
              aria-modal="true"
              aria-labelledby="about-foth-title"
            >
              <button 
                type="button" 
                onClick={onClose} 
                className={styles['close-modal-btn']}
                aria-label="Cerrar modal"
              >
                <X size={20} />
              </button>

              <div className={styles['modal-badge']}>
                <Sparkles size={13} />
                <span>{t('public_invite.exclusive_badge', 'Invitación Privada Exclusiva')}</span>
              </div>
              
              <h2 id="about-foth-title">
                {t('public_invite.modal_title', 'El punto de encuentro para creadores y marcas de excelencia')}
              </h2>
              <p className={styles.lead}>
                {t('public_invite.modal_subtitle', 'FOTH es una plataforma privada donde marcas y creadores conectan con absoluta claridad, contratos transparentes y valor genuino.')}
              </p>

              <div className={styles['features-grid']}>
                <div className={styles['feature-card']}>
                  <div className={styles['icon-box']}><ShieldCheck size={20} /></div>
                  <h4>{t('public_invite.modal_feature_1_title', 'Claridad y Confianza')}</h4>
                  <p>{t('public_invite.modal_feature_1_desc', 'Entiende exactamente qué espera la marca y qué recibirás tú desde el primer momento.')}</p>
                </div>
                <div className={styles['feature-card']}>
                  <div className={styles['icon-box']}><Award size={20} /></div>
                  <h4>{t('public_invite.modal_feature_2_title', 'Sin Comisiones')}</h4>
                  <p>{t('public_invite.modal_feature_2_desc', 'La membresía y participación en colaboraciones para creadores es 100% gratuita.')}</p>
                </div>
                <div className={styles['feature-card']}>
                  <div className={styles['icon-box']}><HeartHandshake size={20} /></div>
                  <h4>{t('public_invite.modal_feature_3_title', 'Relaciones a Largo Plazo')}</h4>
                  <p>{t('public_invite.modal_feature_3_desc', 'Construye conexiones auténticas con hoteles, restaurantes y marcas prestigiosas.')}</p>
                </div>
              </div>

              <div className={styles['modal-footer']}>
                <Button variant="primary" size="md" onClick={onClose}>
                  {t('public_invite.modal_btn_close', 'Entendido')}
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

const PublicCollabLanding = () => {
  const { inviteToken } = useParams();
  const { currentUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('creator');
  
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [error, setError] = useState(null);
  const [decisionState, setDecisionState] = useState('review');
  const [actionLoading, setActionLoading] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const featuredImage = offer?.featured_image_url || offer?.featured_image;
  const additionalImages = useMemo(() => {
    if (!Array.isArray(offer?.additional_images)) return [];
    return offer.additional_images
      .map(img => (typeof img === 'string' ? img : (img?.url || img?.image || img?.file)))
      .filter(Boolean);
  }, [offer?.additional_images]);

  const allImages = useMemo(() => {
    const list = [];
    if (featuredImage) list.push(featuredImage);
    additionalImages.forEach(img => {
      if (!list.includes(img)) list.push(img);
    });
    return list;
  }, [featuredImage, additionalImages]);

  const compensationMode = offer?.compensation_mode || 'exchange';
  const cashAmount = Number(offer?.cash_compensation || 0);
  const exchangeAmount = Number(offer?.value || 0);
  const currency = offer?.cash_currency || 'USD';

  const compensationLabel = compensationMode === 'paid'
    ? t('public_invite.compensation_label', 'Compensación')
    : compensationMode === 'hybrid'
      ? t('public_invite.compensation_hybrid', 'Compensación + Intercambio')
      : t('public_invite.compensation_exchange', 'Intercambio');

  const compensationValue = compensationMode === 'paid'
    ? `${currency} ${cashAmount.toLocaleString()}`
    : compensationMode === 'hybrid'
      ? `${currency} ${cashAmount.toLocaleString()} + $${exchangeAmount.toLocaleString()} ${t('public_invite.equiv', 'equiv.')}`
      : `$${exchangeAmount.toLocaleString()} ${t('public_invite.equiv', 'equiv.')}`;

  const followerRequirementLabels = {
    '1': t('public_invite.followers_any', 'Sin mínimo de seguidores'),
    '2': t('public_invite.followers_friends', 'Creadores Friends of the House'),
    '3': t('public_invite.followers_mid', 'Entre 3,000 y 15,000 seguidores'),
    '4': t('public_invite.followers_high', 'Más de 15,000 seguidores'),
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!inviteToken) {
          setError(t('public_invite.error_desc', 'Esta invitación no es válida o ha expirado.'));
          setLoading(false);
          return;
        }

        // Store invite token in sessionStorage to guarantee persistence
        sessionStorage.setItem('foth_invite_token', inviteToken);

        const response = await getRecipientInviteInfo(inviteToken);
        setOffer(response.data);
        const inviteStatus = response.data?.invite_status;
        setDecisionState(inviteStatus === 'declined' || inviteStatus === 'accepted' ? inviteStatus : 'review');
        
        const query = new URLSearchParams(window.location.search);
        const channel = query.get('c') || query.get('channel') || 'general';
        sessionStorage.setItem('foth_invite_channel', channel);
      } catch (err) {
        setError(t('public_invite.error_desc', 'Esta invitación no es válida o ha expirado.'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [inviteToken, t]);

  const handleAcceptClick = async () => {
    if (decisionState === 'declined') {
      toast.info(t('public_invite.toast_declined', 'Esta invitación ya fue rechazada.'));
      return;
    }

    if (isAuthenticated) {
      if (currentUser?.user_type === 'creator') {
        if (inviteToken) {
          try {
            setActionLoading('accept');
            await acceptRecipientInvite(inviteToken);
            sessionStorage.removeItem('foth_invite_token');
            announcePwaInstallOpportunity('public_invite_accepted');
          } catch (err) {
            toast.error(err.response?.data?.error || t('public_invite.toast_validate_error', 'No pudimos validar esta invitación.'));
            return;
          } finally {
            setActionLoading(null);
          }
        }
        navigate(`/creator/offers/${offer.id}`);
      } else {
        toast.info(t('public_invite.toast_creators_only', 'Las colaboraciones son para creadores de contenido.'));
      }
    } else {
      navigate(inviteToken ? `/register?invite_token=${inviteToken}` : '/register');
    }
  };

  const handleLoginClick = () => {
    navigate(inviteToken ? `/login?invite_token=${inviteToken}` : '/login');
  };

  const handleDeclineClick = async () => {
    if (!inviteToken || decisionState === 'declined') return;

    try {
      setActionLoading('decline');
      await declineRecipientInvite(inviteToken);
      sessionStorage.removeItem('foth_invite_token');
      setDecisionState('declined');
      toast.success(t('public_invite.toast_declined_success', 'Invitación rechazada. Gracias por responder.'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('public_invite.toast_decline_error', 'No pudimos rechazar esta invitación.'));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className={styles['loading-container']}>
        <div className={styles['loader-visual']}>
          <span className={styles.loader_logo}>FOTH</span>
          <div className={styles.spinner}></div>
        </div>
        <p>{t('public_invite.loading_title', 'Preparando tu invitación exclusiva...')}</p>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className={styles['error-container']}>
        <div className={styles['error-icon-box']}>
          <AlertCircle size={32} />
        </div>
        <h2>{t('public_invite.error_title', 'Invitación No Disponible')}</h2>
        <p>{error || t('public_invite.error_desc', 'Esta invitación no es válida, ha expirado o ya fue procesada.')}</p>
        <Button variant="outline" onClick={() => navigate('/')}>
          {t('public_invite.btn_home', 'Volver al inicio')}
        </Button>
      </div>
    );
  }

  const isFull = offer.remaining_slots === 0;
  const isDeclined = decisionState === 'declined';
  const isAccepted = decisionState === 'accepted';

  const handleAcceptedInviteClick = () => {
    if (isAuthenticated && currentUser?.user_type === 'creator') {
      navigate(`/creator/collabs/${offer.id}`);
      return;
    }
    navigate(inviteToken ? `/login?invite_token=${inviteToken}` : '/login');
  };

  return (
    <div className={styles['public-collab-landing']}>
      <AboutFothModal open={aboutOpen} onClose={() => setAboutOpen(false)} />
      
      {/* FLOATING LUXURY NAVBAR */}
      <header className={styles['floating-navbar']}>
        <div className={styles.container}>
          <div className={styles['nav-brand-group']}>
            <Link to="/" className={styles.logo} aria-label="FOTH Inicio">
              FOTH
            </Link>
            <span className={styles['navbar-exclusive-tag']}>
              ✦ {t('public_invite.exclusive_badge', 'Invitación Privada Exclusiva')}
            </span>
          </div>

          <div className={styles['nav-actions']}>
            <button 
              type="button"
              className={styles['btn-about']}
              onClick={() => setAboutOpen(true)}
            >
              <Sparkles size={14} />
              <span>{t('public_invite.what_is_foth', '¿Qué es FOTH?')}</span>
            </button>

          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className={styles['landing-hero']}>
        <div className={styles['hero-ambient-glow']} />
        <div className={styles.container}>
          {/* Host Business Capsule */}
          <motion.div 
            className={styles['business-pill']}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: luxuryEase }}
          >
            {offer.business_logo_url ? (
              <img src={offer.business_logo_url} alt="" className={styles['brand-logo']} />
            ) : (
              <div className={styles.abbr}>{offer.business_name ? offer.business_name[0] : 'B'}</div>
            )}
            <div className={styles['business-info-meta']}>
              <span className={styles['business-name']}>{offer.business_name}</span>
              <span className={styles['invite-text']}>{t('public_invite.invited_by', 'te ha invitado a colaborar')}</span>
            </div>
          </motion.div>
          
          {/* Headline */}
          <motion.h1 
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5, ease: luxuryEase }}
          >
            {offer.title}
          </motion.h1>

          {/* Subtitle / Personal Note */}
          <motion.p
            className={styles['hero-subtext']}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {t('public_invite.personal_invitation_note', {
              business: offer.business_name,
              defaultValue: `${offer.business_name} te ha seleccionado para participar de forma privada en esta colaboración exclusiva.`
            })}
          </motion.p>
          
          {/* Meta Information Badges */}
          <motion.div 
            className={styles['hero-meta']}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
          >
            {offer.country_name && (
              <div className={styles['meta-tag']}>
                <MapPin size={14} />
                <span>{offer.country_name}</span>
              </div>
            )}
            {offer.from_date && offer.to_date && (
              <div className={styles['meta-tag']}>
                <CalendarDays size={14} />
                <span>
                  {new Date(offer.from_date).toLocaleDateString()} — {new Date(offer.to_date).toLocaleDateString()}
                </span>
              </div>
            )}
            {offer.expires_at && (
              <div className={styles['meta-tag']}>
                <Clock size={14} />
                <span>{t('public_invite.valid_until', { date: new Date(offer.expires_at).toLocaleDateString() })}</span>
              </div>
            )}
          </motion.div>

          {/* Featured / Multi-Image Showcase */}
          {allImages.length > 0 && (
            <>
              <motion.div 
                className={styles['hero-image-wrapper']}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6, ease: luxuryEase }}
              >
                <img src={allImages[activeImageIndex] || featuredImage} alt={offer.title} className={styles['hero-image']} />
                <div className={styles['hero-image-overlay']} />
                {allImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      className={styles['carousel-nav-prev']}
                      onClick={() => setActiveImageIndex(prev => (prev === 0 ? allImages.length - 1 : prev - 1))}
                      aria-label={t('public_invite.prev_image', 'Imagen anterior')}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      type="button"
                      className={styles['carousel-nav-next']}
                      onClick={() => setActiveImageIndex(prev => (prev === allImages.length - 1 ? 0 : prev + 1))}
                      aria-label={t('public_invite.next_image', 'Siguiente imagen')}
                    >
                      <ChevronRight size={20} />
                    </button>
                    <span className={styles['carousel-badge']}>
                      {activeImageIndex + 1} / {allImages.length}
                    </span>
                  </>
                )}
              </motion.div>

              {allImages.length > 1 && (
                <div className={styles['thumbnails-bar']} role="group" aria-label={t('public_invite.thumbnails_aria', 'Miniaturas de imágenes')}>
                  {allImages.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={clsx(styles['thumbnail-btn'], idx === activeImageIndex && styles['active-thumb'])}
                      onClick={() => setActiveImageIndex(idx)}
                      aria-label={t('public_invite.select_image', { index: idx + 1, defaultValue: `Seleccionar imagen ${idx + 1}` })}
                    >
                      <img src={img} alt="" />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Highlights Showcase Bar */}
          <div className={styles['highlights-grid']}>
            <div className={clsx(styles['highlight-card'], styles.accent)}>
              <div className={styles.icon_box}><Sparkles size={22} /></div>
              <div className={styles.content}>
                <label>{compensationLabel}</label>
                <div className={styles.value}>{compensationValue}</div>
              </div>
            </div>

            <div className={styles['highlight-card']}>
              <div className={styles.icon_box}><Users size={22} /></div>
              <div className={styles.content}>
                <label>{t('public_invite.slots_label', 'Cupos disponibles')}</label>
                <div className={styles.value}>
                  {offer.remaining_slots ?? offer.quantity ?? 1}{' '}
                  <small>{t('public_invite.slots_creators', { count: offer.remaining_slots ?? offer.quantity ?? 1 })}</small>
                </div>
              </div>
            </div>

            <div className={styles['highlight-card']}>
              <div className={styles.icon_box}><Layers size={22} /></div>
              <div className={styles.content}>
                <label>{t('public_invite.deliverables_title', 'Entregables')}</label>
                <div className={styles.value}>
                  {offer.exchange_options?.length || 1}{' '}
                  <small>{t('public_invite.deliverables_count', { count: offer.exchange_options?.length || 1 })}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN TWO-COLUMN EDITORIAL LAYOUT */}
      <div className={styles.container}>
        <div className={styles['main-content-layout']}>
          {/* LEFT COLUMN: COLLABORATION DOSSIER */}
          <main className={styles['content-body']}>
            {/* Section 1: Campaign Concept */}
            <article className={styles['editorial-card']}>
              <div className={styles['card-header-bar']}>
                <div className={styles['header-icon']}><Info size={16} /></div>
                <h3>{t('public_invite.about_collab', 'Sobre la Campaña')}</h3>
              </div>
              <div className={styles['card-body']}>
                <RichTextDisplay content={offer.description} className={styles['rich-content']} />
              </div>
            </article>

            {/* Section 2: Deliverables & Expectations */}
            <article className={styles['editorial-card']}>
              <div className={styles['card-header-bar']}>
                <div className={styles['header-icon']}><CheckCircle2 size={16} /></div>
                <h3>{t('public_invite.deliverables_title', 'Lo que esperamos de ti')}</h3>
              </div>
              <div className={styles['card-body']}>
                <div className={styles['deliverables-row']}>
                  {offer.exchange_options && offer.exchange_options.map((opt) => {
                    const isVideo = opt.name?.toLowerCase().includes('video') || opt.name?.toLowerCase().includes('reel');
                    return (
                      <span key={opt.code || opt.name} className={styles['deliverable-pill']}>
                        {isVideo ? <Video size={14} /> : <ImageIcon size={14} />}
                        <span>{opt.name}</span>
                      </span>
                    );
                  })}
                </div>
                {offer.terms_and_conditions && (
                  <div className={styles['terms-wrapper']}>
                    <h4 className={styles['terms-heading']}>{t('public_invite.terms_title', 'Términos y Condiciones')}</h4>
                    <RichTextDisplay content={offer.terms_and_conditions} className={styles['rich-content-terms']} />
                  </div>
                )}
              </div>
            </article>

            {/* Gallery Showcase Card */}
            {allImages.length > 1 && (
              <article className={styles['editorial-card']}>
                <div className={styles['card-header-bar']}>
                  <div className={styles['header-icon']}><ImageIcon size={16} /></div>
                  <h3>{t('public_invite.gallery_title', 'Galería de imágenes')}</h3>
                </div>
                <div className={styles['card-body']}>
                  <div className={styles['gallery-grid']}>
                    {allImages.map((img, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={clsx(styles['gallery-item'], idx === activeImageIndex && styles.active)}
                        onClick={() => setActiveImageIndex(idx)}
                        aria-label={t('public_invite.select_image', { index: idx + 1, defaultValue: `Ver imagen ${idx + 1}` })}
                      >
                        <img src={img} alt={`Imagen ${idx + 1}`} />
                        {idx === 0 && <span className={styles['gallery-featured-tag']}>{t('public_invite.featured_badge', 'Portada')}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              </article>
            )}

            {/* Section 3: Creator Journey Stepper */}
            <article className={clsx(styles['editorial-card'], styles['journey-card'])}>
              <div className={styles['card-header-bar']}>
                <div className={styles['header-icon']}><Compass size={16} /></div>
                <div>
                  <h3>{t('public_invite.journey_title', 'Tu camino en esta colaboración')}</h3>
                  <p className={styles['section-subtitle']}>
                    {t('public_invite.journey_subtitle', 'Diseñado para creadores seleccionados: sin listas de espera ni fricciones.')}
                  </p>
                </div>
              </div>
              <div className={styles['card-body']}>
                <div className={styles['journey-timeline']}>
                  <div className={styles['journey-step']}>
                    <div className={styles['step-indicator']}>01</div>
                    <div className={styles['step-content']}>
                      <h4>{t('public_invite.step1_title', '1. Revisa y acepta')}</h4>
                      <p>{t('public_invite.step1_desc', 'Evalúa el brief, la compensación y los entregables propuestos sin compromiso.')}</p>
                    </div>
                  </div>

                  <div className={styles['journey-step']}>
                    <div className={styles['step-indicator']}>02</div>
                    <div className={styles['step-content']}>
                      <h4>{t('public_invite.step2_title', '2. Activa tu acceso')}</h4>
                      <p>{t('public_invite.step2_desc', 'Crea tu perfil gratuito en 1 minuto o inicia sesión para vincular la invitación.')}</p>
                    </div>
                  </div>

                  <div className={styles['journey-step']}>
                    <div className={styles['step-indicator']}>03</div>
                    <div className={styles['step-content']}>
                      <h4>{t('public_invite.step3_title', '3. Produce y cobra')}</h4>
                      <p>{t('public_invite.step3_desc', 'Coordina directamente con la marca, entrega tu contenido y recibe tu pago o regalo.')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </article>

            {/* Section 4: FOTH Guarantees & Prestige */}
            <div className={styles['guarantees-grid']}>
              <div className={styles['guarantee-item']}>
                <div className={styles['guarantee-icon']}><ShieldCheck size={18} /></div>
                <span>{t('public_invite.guarantee_free', '100% Gratuito para creadores (sin tarifas ni comisiones)')}</span>
              </div>
              <div className={styles['guarantee-item']}>
                <div className={styles['guarantee-icon']}><HeartHandshake size={18} /></div>
                <span>{t('public_invite.guarantee_direct', 'Trato directo con la marca y términos claros')}</span>
              </div>
              <div className={styles['guarantee-item']}>
                <div className={styles['guarantee-icon']}><Lock size={18} /></div>
                <span>{t('public_invite.guarantee_secure', 'Colaboración privada protegida por la plataforma')}</span>
              </div>
            </div>
          </main>

          {/* RIGHT COLUMN: STICKY DECISION CARD */}
          <aside className={styles['sidebar-col']}>
            <div className={styles.sticky}>
              {/* Requirements Capsule */}
              <div className={styles['requirements-card']}>
                <h4>{t('public_invite.requirements_title', 'Requisitos del Creador')}</h4>
                <div className={styles['req-list']}>
                  <div className={styles['req-item']}>
                    <CheckCircle2 size={15} className={styles.dot} />
                    <span>{followerRequirementLabels[offer.followers_requirements] || t('public_invite.followers_default', 'Requisitos de audiencia según campaña')}</span>
                  </div>
                  {offer.target_options && offer.target_options.map((opt) => (
                    <div key={opt.code || opt.name} className={styles['req-item']}>
                      <Sparkles size={15} className={styles.dot} />
                      <span>{opt.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Luxury Action Decision Card */}
              <div className={styles['cta-card-luxury']}>
                <div className={styles['status-pill-row']}>
                  <span className={clsx(
                    styles['decision-pill'],
                    isDeclined && styles.declined,
                    isAccepted && styles.accepted,
                    isFull && styles.full,
                  )}>
                    {isDeclined 
                      ? t('public_invite.status_declined', 'Invitación Declinada') 
                      : isAccepted
                        ? t('public_invite.status_accepted', 'Ya estás participando')
                      : isFull 
                        ? t('public_invite.status_full', 'Cupos Completados')
                        : t('public_invite.status_received', 'Invitación Recibida')}
                  </span>
                </div>

                {isDeclined ? (
                  <div className={styles['declined-state-body']}>
                    <div className={styles['declined-icon']}>
                      <XCircle size={28} />
                    </div>
                    <h3>{t('public_invite.card_declined_title', 'Gracias por responder')}</h3>
                    <p>{t('public_invite.card_declined_desc', 'Esta invitación ha sido marcada como rechazada. No se requiere ninguna acción adicional.')}</p>
                  </div>
                ) : isAccepted ? (
                  <div className={styles['declined-state-body']}>
                    <div className={styles['accepted-icon']}>
                      <CheckCircle2 size={28} />
                    </div>
                    <h3>{t('public_invite.card_accepted_title', 'Ya estás participando')}</h3>
                    <p>{t('public_invite.card_accepted_desc', 'Esta invitación ya fue aceptada. Puedes continuar con tu colaboración desde tu espacio de creador.')}</p>
                    <div className={styles['decision-actions']}>
                      <Button variant="primary" fullWidth size="lg" onClick={handleAcceptedInviteClick}>
                        {isAuthenticated
                          ? t('public_invite.btn_view_collab', 'Ver colaboración')
                          : t('public_invite.btn_login_to_collab', 'Iniciar sesión para continuar')}
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className={styles['active-state-body']}>
                    <h3>{t('public_invite.card_accept_title', 'Acepta tu invitación privada')}</h3>
                    <p>{t('public_invite.card_accept_desc', 'Al aceptar, accederás prioritariamente a esta colaboración sin pasar por lista de espera pública.')}</p>
                    
                    {offer.invited_email && (
                      <div className={styles['reserved-email-box']}>
                        <span className={styles['reserved-label']}>{t('public_invite.reserved_for', 'Invitación reservada para:')}</span>
                        <strong className={styles['reserved-email']}>{offer.invited_email}</strong>
                      </div>
                    )}

                    {isFull && (
                      <p className={styles['invite-closed-note']}>
                        {t('public_invite.offer_closed', 'Esta colaboración ya no cuenta con cupos disponibles.')}
                      </p>
                    )}

                    <div className={styles['decision-actions']}>
                      <Button
                        variant="primary"
                        fullWidth
                        size="lg"
                        loading={actionLoading === 'accept'}
                        disabled={isFull}
                        onClick={handleAcceptClick}
                      >
                        <span>
                          {isAuthenticated
                            ? t('public_invite.btn_accept_authenticated', 'Aceptar colaboración')
                            : t('public_invite.btn_accept_register', 'Aceptar invitación')}
                        </span>
                        <ArrowRight className="ml-2" size={18} />
                      </Button>

                      {!isAuthenticated && (
                        <button
                          type="button"
                          className={styles['btn-login-alternate']}
                          onClick={handleLoginClick}
                        >
                          {t('public_invite.btn_login_existing', '¿Ya tienes cuenta en FOTH? Inicia sesión')}
                        </button>
                      )}

                      <Button
                        variant="ghost"
                        fullWidth
                        size="md"
                        loading={actionLoading === 'decline'}
                        onClick={handleDeclineClick}
                        className={styles['btn-decline']}
                      >
                        {t('public_invite.btn_decline', 'Declinar amablemente')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* MOBILE FLOATING CTA DOCK */}
      {!isDeclined && (
        <div className={styles['mobile-cta-shell']}>
          <div className={styles['mobile-cta-summary']}>
            <span className={styles['mobile-brand-label']}>{offer.business_name}</span>
            <span className={styles['mobile-val-label']}>{compensationValue}</span>
          </div>
          <div className={styles['mobile-btn-row']}>
            <Button
              variant="primary"
              fullWidth
              size="lg"
              loading={actionLoading === 'accept'}
              disabled={!isAccepted && isFull}
              onClick={isAccepted ? handleAcceptedInviteClick : handleAcceptClick}
            >
              {isAccepted
                ? (isAuthenticated
                  ? t('public_invite.btn_view_collab', 'Ver colaboración')
                  : t('public_invite.btn_login_to_collab', 'Iniciar sesión para continuar'))
                : (isAuthenticated
                  ? t('public_invite.btn_accept_authenticated', 'Aceptar colaboración')
                  : t('public_invite.btn_accept_register', 'Aceptar invitación'))}
            </Button>
            {!isAccepted && (
              <Button
                variant="ghost"
                size="lg"
                loading={actionLoading === 'decline'}
                onClick={handleDeclineClick}
                className={styles['btn-mobile-decline']}
                aria-label="Declinar invitación"
              >
                <X size={18} />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicCollabLanding;
